/**
 * Trade book + same-IP + top gainers/losers — shared CSV/session/form helpers in utils/*.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { api } from "../../utils/api";
import { errorLog } from "utils/logger";
import { downloadCsvFromObjects } from "utils/csvExport";
import { extractSelectOrPrimitive } from "utils/formValues";
import { safeParseInt } from "utils/numberParse";
import { getNormalizedThunkSession } from "utils/tradeSession";
import { formatApiDateYmd } from "utils/dateFormats";
import { axiosRejectPayload } from "utils/httpErrors";

// ── initialState ──────────────────────────────────────────────────────────────
// ✅ FIX 2: Date objects are NOT serializable — Redux Toolkit warns and may
//    corrupt them. Store ISO strings instead.
const today = moment().format("YYYY-MM-DD");
const initialState = {
  sortTradeBookArr           : [],
  tradeBookList              : undefined,
  tradeBookLoading           : false,
  tradeSameIpLoading         : false,
  tradeSameIpSearchText      : "",
  tradeSameIpList            : [],
  topGainersAndLosersData    : [],
  topGainersAndLosersLoading : false,
  tradeBookSearchText        : "",
  currentPageTradeBook       : 1,
  pageSizeTradeBook          : 10,
  totalRecordTradeBook       : 0,
  totalRecordTradeSameIp     : 0,
  startApiCallTradeBook      : true,
  isTradeBookUpdated         : false,
  filterStartDate            : today,           // ✅ was: new Date()
  filterEndDate              : today,           // ✅ was: new Date()
  sameIpFilterStartDate      : today,           // ✅ was: new Date()
  sameIpFilterEndDate        : today,           // ✅ was: new Date()
  tradeBookFilterValue       : { startDate: today, endDate: today },  // ✅ was: new Date()
  tradeSameIpFilterValue     : { startDate: today, endDate: today },  // ✅ was: new Date()
  isTradeBookGet             : false,
  isTradeSameIdGet           : false,
};

// ─────────────────────────────────────────────────────────────────────────────
// getTradeBookList → POST trade/get-trade-book
// ─────────────────────────────────────────────────────────────────────────────
export const getTradeBookList = createAsyncThunk(
  "trade_book/get",
  async (data, { rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId = safeParseInt(data.userId || session.userId);

      if (userId === 0) {
        console.error("❌ userId is 0 — user is not logged in or session is missing");
      }

      const roles = session.roles;

      const {
        searchText, pageNo, pageSize, appendData,
        buyOrSell, client, segment, subBrokerId, symbol,
        endDate, startDate, partnerId, maintenanceType, authorityId,
        isExcludeSB,
        // ✅ FIX 3: TradeBook.js now sends these as primitives directly
        orderType, segmentId, clientId: clientIdProp,
        tradeOption, symbolVal,
      } = data;

      // ✅ extractSelectOrPrimitive() now handles both react-select objects AND primitives safely
      const rawClientId = clientIdProp !== undefined ? clientIdProp : extractSelectOrPrimitive(client);
      const clientId = rawClientId !== null
        ? safeParseInt(rawClientId)
        : roles[0] === "User"
          ? userId
          : null;

      const payload = {
        userId      : userId,
        pageNo      : safeParseInt(pageNo)   || 1,
        pageSize    : safeParseInt(pageSize) || 50000,
        // ✅ FIX 3: support both { segmentId: primitive } and { segment: reactSelectObj }
        segmentId   : segmentId   !== undefined
                        ? (segmentId !== null ? safeParseInt(segmentId) : null)
                        : extractSelectOrPrimitive(segment) !== null ? safeParseInt(extractSelectOrPrimitive(segment)) : null,
        clientId,
        startDate   : formatApiDateYmd(startDate),
        endDate     : formatApiDateYmd(endDate),
        // ✅ FIX 3: support both { symbol: primitive } and { symbol: reactSelectObj }
        symbol      : extractSelectOrPrimitive(symbol) ?? null,
        searchText  : searchText || "",
      };

      // History Module Step 6 — Trade Book cut over to Deals
      const { data: res } = await api.post("deals/get", payload);

      if (res.status === 200) {
        const records = (res.data?.records || []).map((item) => ({
          ...item,
          tradeNumber   : item.id,
          client        : item.clientName || "",
          script        : item.displayName || item.symbol || "",
          orderTypeName : item.dealTypeName || "",
          lot           : item.volumeLots,
          quantity      : item.volumeQty,
          rate          : item.price,
          addTime       : item.dealTime,
          tradeDate     : item.dealTime ? String(item.dealTime).slice(0, 10) : "",
          tradeTime     : item.dealTime || "",
          tradeFrom     : item.tradeFrom || "",
          tradeTypeName : item.entryName || "",
          segmentName   : item.segment || "",
          netRate       : item.price,
        }));
        return { data: { ...res.data, records }, appendData };
      }
      return rejectWithValue(res);

    } catch (err) {
      errorLog("Trade Book error:", err?.response?.status, err?.response?.data);
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// exportTradeBook
// ─────────────────────────────────────────────────────────────────────────────
export const exportTradeBook = createAsyncThunk(
  "exportPDf",
  async (data, { rejectWithValue }) => {
    try {
      const session  = getNormalizedThunkSession();
      const userId   = safeParseInt(data.userId || session.userId);
      const roles    = session.roles;

      const { searchText, pageNo, pageSize, appendData,
              buyOrSell, orderType, client, clientId: clientIdProp,
              segment, segmentId, subBrokerId,
              symbol, endDate, startDate } = data;

      const rawClientId = clientIdProp !== undefined ? clientIdProp : extractSelectOrPrimitive(client);
      const clientId = rawClientId !== null
        ? safeParseInt(rawClientId)
        : roles[0] === "User" ? userId : null;

      const payload = {
        userId,
        pageNo      : safeParseInt(pageNo)   || 1,
        pageSize    : safeParseInt(pageSize) || 200000,
        segmentId   : segmentId !== undefined
                        ? (segmentId !== null ? safeParseInt(segmentId) : null)
                        : extractSelectOrPrimitive(segment) !== null ? safeParseInt(extractSelectOrPrimitive(segment)) : null,
        clientId,
        startDate   : formatApiDateYmd(startDate),
        endDate     : formatApiDateYmd(endDate),
        symbol      : extractSelectOrPrimitive(symbol) ?? null,
        searchText  : searchText || "",
      };

      const { data: res } = await api.post("deals/get", payload);

      if (res.status === 200) {
        const rows = (res.data?.records || []).map(item => ({
          deal_no         : item.id,
          order_no        : item.orderId,
          segment         : item.segment,
          client_name     : item.clientName,
          script_name     : item.displayName || item.symbol,
          buysell         : item.dealTypeName,
          entry           : item.entryName,
          lot             : item.volumeLots,
          qty             : item.volumeQty,
          rate            : item.price,
          commission      : item.commission,
          profit          : item.profit,
          deal_time       : item.dealTime,
        }));
        downloadCsvFromObjects(rows, "Deals");
        return { data: res.data, appendData };
      }
      return rejectWithValue(res);
    } catch (err) {
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// getTopGainersAndLosers
// ─────────────────────────────────────────────────────────────────────────────
export const getTopGainersAndLosers = createAsyncThunk(
  "user/get-top-gainers-and-losers",
  async (props, { rejectWithValue }) => {
    try {
      const { userId } = getNormalizedThunkSession();
      const { clientId, userTop, gainersorLosers } = props;
      const uid = clientId?.value ? safeParseInt(clientId.value) : userId;
      const { data: res } = await api.get(
        `reports/get-top-gainers-and-losers/${uid}/${userTop}/${gainersorLosers}`
      );
      if (res.status === 200) return { data: res.data };
      return rejectWithValue(res);
    } catch (err) {
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// getTradeSameIpList
// ─────────────────────────────────────────────────────────────────────────────
export const getTradeSameIpList = createAsyncThunk(
  "trade_book/get-trade-sameip-list",
  async (data, { rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(data.userId || session.userId);

      const { searchText, pageNo, pageSize, client,
              segment, symbol, endDate, startDate, authority } = data;

      const payload = {
        userId,
        pageNo      : safeParseInt(pageNo)   || 1,
        pageSize    : safeParseInt(pageSize) || 50000,
        clientId    : extractSelectOrPrimitive(client)    !== null ? safeParseInt(extractSelectOrPrimitive(client))    : null,
        segmentId   : extractSelectOrPrimitive(segment)   !== null ? safeParseInt(extractSelectOrPrimitive(segment))   : null,
        authorityId : extractSelectOrPrimitive(authority) !== null ? safeParseInt(extractSelectOrPrimitive(authority)) : null,
        symbol      : extractSelectOrPrimitive(symbol)    ?? null,
        startDate   : formatApiDateYmd(startDate),
        endDate     : formatApiDateYmd(endDate),
        searchText  : searchText    || "",
      };

      const { data: res } = await api.post("trade/get-trades-for-same-ip", payload);
      if (res.status === 200) return { data: res.data };
      return rejectWithValue(res);
    } catch (err) {
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
export const tradeBookSlice = createSlice({
  name: "tradeBook",
  initialState,
  reducers: {
    setSortTradeBook           : (s, { payload }) => { s.sortTradeBookArr          = payload; },
    setTradeBookSearchText     : (s, { payload }) => { s.tradeBookSearchText       = payload; },
    setTradeSameIpSearchText   : (s, { payload }) => { s.tradeSameIpSearchText     = payload; },
    setStartPageTradeBook      : (s, { payload }) => { s.currentPageTradeBook      = payload; },
    setFilterStartDate         : (s, { payload }) => { s.filterStartDate           = payload; },
    setFilterEndDate           : (s, { payload }) => { s.filterEndDate             = payload; },
    setSameIpFilterStartDate   : (s, { payload }) => { s.sameIpFilterStartDate     = payload; },
    setSameIpFilterEndDate     : (s, { payload }) => { s.sameIpFilterEndDate       = payload; },
    setTotalRecordTradeSameIp  : (s, { payload }) => { s.totalRecordTradeSameIp    = payload; },
    setTradeBookList           : (s, { payload }) => { s.tradeBookList             = payload; },
    setTradeBookFilterValue    : (s, { payload }) => { s.tradeBookFilterValue      = payload; },
    setTradeSameIpFilterValue  : (s, { payload }) => { s.tradeSameIpFilterValue    = payload; },
    setTradeSameIpList         : (s, { payload }) => { s.tradeSameIpList           = payload; },
    setTopGainersAndLosersData : (s, { payload }) => { s.topGainersAndLosersData   = payload; },
    setTotalRecordTradeBook    : (s, { payload }) => { s.totalRecordTradeBook      = payload; },
    setIsTradeBookGet          : (s, { payload }) => { s.isTradeBookGet            = payload; },
    setIsTradeSameIdGet        : (s, { payload }) => { s.isTradeSameIdGet          = payload; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTradeBookList.pending,   (s) => { s.tradeBookLoading = true; })
      .addCase(getTradeBookList.rejected,  (s) => { s.tradeBookList = []; s.tradeBookLoading = false; s.isTradeBookGet = false; })
      .addCase(getTradeBookList.fulfilled, (s, { payload }) => {
        const records = payload.data?.records || [];
        s.tradeBookList = payload.appendData
          ? [...(s.tradeBookList || []), ...records].map(i => ({ ...i, isSelect: false }))
          : records.map(i => ({ ...i, isSelect: false }));
        s.totalRecordTradeBook  = payload.data?.totalRecords || 0;
        s.startApiCallTradeBook = false;
        s.tradeBookLoading      = false;
        s.isTradeBookGet        = false;
      })
      .addCase(getTopGainersAndLosers.pending,   (s) => { s.topGainersAndLosersLoading = true; })
      .addCase(getTopGainersAndLosers.rejected,  (s) => { s.topGainersAndLosersData = []; s.topGainersAndLosersLoading = false; })
      .addCase(getTopGainersAndLosers.fulfilled, (s, { payload }) => { s.topGainersAndLosersData = payload.data || []; s.topGainersAndLosersLoading = false; })
      .addCase(getTradeSameIpList.pending,   (s) => { s.tradeSameIpLoading = true; })
      .addCase(getTradeSameIpList.rejected,  (s) => { s.tradeSameIpList = []; s.tradeSameIpLoading = false; })
      .addCase(getTradeSameIpList.fulfilled, (s, { payload }) => {
        s.tradeSameIpList        = payload.data?.records      || [];
        s.totalRecordTradeSameIp = payload.data?.totalRecords || 0;
        s.tradeSameIpLoading     = false;
      });
  },
});

export const {
  setSortTradeBook, setTradeBookSearchText, setStartPageTradeBook,
  setFilterStartDate, setFilterEndDate, setTradeBookList,
  setTradeBookFilterValue, setTradeSameIpFilterValue, setIsTradeSameIdGet,
  setTradeSameIpSearchText, setSameIpFilterEndDate, setSameIpFilterStartDate,
  setTotalRecordTradeBook, setTotalRecordTradeSameIp, setTopGainersAndLosersData,
  setIsTradeBookGet, setTradeSameIpList,
} = tradeBookSlice.actions;

export default tradeBookSlice.reducer;